import { Skeleton, Icon, Tag } from '@distribusion/owl-frontend-ui'
import { useQuery } from '@tanstack/react-query'
import cn from 'classnames'
import React, { useMemo } from 'react'

import bem from '@lib/bem'
import utils from '@lib/utils'
import { useSeatSelectionContext } from '@pages/Checkout/SeatSelection/NewSeats/context/seatSelection'

import '@pages/Checkout/SeatSelection/NewSeats/SeatsPreview.scss'

interface CarSeatsPreviewProps {
  seats: Seat.Selected[]
  direction: Seat.TripDirection
  car?: Seat.Train.Car
  segmentIndex?: number
}

const CarSeatsPreview: React.FC<CarSeatsPreviewProps> = ({ car, seats, segmentIndex, direction }) => {
  const { layoutQuery } = useSeatSelectionContext()
  const { data: layout, isLoading } = useQuery<Seat.Train.Layout | undefined>(
    useMemo(
      () => ({
        ...layoutQuery(car?.layout || '', direction, segmentIndex ?? 0, car?.index ?? 0),
        enabled: !!car,
      }),
      [car, direction, layoutQuery, segmentIndex],
    ),
  )

  return (
    <Skeleton.Text
      placeholder={
        <>
          {car && (
            <span className={bem('new-seats', 'preview-carriage-seats-info')}>
              <Icon name="carriage" size="medium" />
              <span>{car.label}</span>
            </span>
          )}
          <span className={bem('new-seats', 'preview-carriage-seats-info')}>
            <Icon name="place" size="medium" />
            <Skeleton.Item height={14} width={14} />
          </span>
        </>
      }
      loading={isLoading}
    >
      {() => (
        <div className={bem('new-seats', 'preview-carriage-seats-by-car')}>
          {car && (
            <span className={bem('new-seats', 'preview-carriage-seats-info')}>
              <Icon name="carriage" size="medium" />
              <span>{car.label}</span>
            </span>
          )}
          <span className={bem('new-seats', 'preview-carriage-seats-info')}>
            <Icon name="place" size="medium" />
            <span>
              {seats.map(seat => layout?.seats.find(({ code }) => seat.code === code)?.label || seat.label).join(', ')}
            </span>
          </span>
        </div>
      )}
    </Skeleton.Text>
  )
}

interface FarePreviewProps {
  fareClass?: FareClass
  scheme?: Seat.Train.Segment
  direction: Seat.TripDirection
  fareSeats: Record<number | 'no-car', Seat.Selected[]>
}

const FarePreview: React.FC<FarePreviewProps> = ({ fareClass, fareSeats, scheme, direction }) => (
  <div className={cn(bem('new-seats', 'preview-carriage-seats-by-fare'), 'row', 'items-center')}>
    {fareClass && (
      <Tag className={bem('new-seats', 'preview-carriage-seats-by-fare-badge')}>
        <Icon name="seat" size="medium" />
        {fareClass.shortName || fareClass.name}
      </Tag>
    )}
    {Object.entries(fareSeats).map(([carIndex, seats]) => (
      <CarSeatsPreview
        car={carIndex !== 'no-car' ? scheme?.cars.find(car => car.index === Number(carIndex)) : undefined}
        direction={direction}
        segmentIndex={scheme?.index}
        seats={seats}
        key={carIndex}
      />
    ))}
  </div>
)

interface SeatsPreviewProps {
  segment: Segment
  scheme?: Seat.Train.Segment
  connectionFareClass: string
  seats: Seat.Selected[]
  direction: Seat.TripDirection
  className?: string
}

export const SeatsPreview: React.FC<SeatsPreviewProps> = ({
  segment,
  seats,
  scheme,
  className,
  direction,
  connectionFareClass,
}) => {
  const groupedSeats = useMemo(() => {
    const byFares = utils.array.groupBy(seats, seat => seat.fareClass || connectionFareClass || 'no-fare')
    return utils.object.compact(
      utils.object.map(byFares, seats => seats && utils.array.groupBy(seats, seat => seat.carIndex ?? 'no-car')),
    )
  }, [connectionFareClass, seats])

  return (
    <div data-tag="new-seats-preview" className={cn(bem('new-seats', 'preview'), 'body-14', 'column', className)}>
      {Object.entries(groupedSeats).map(([fareClass, fareSeats]) => {
        return (
          <FarePreview
            key={fareClass}
            scheme={scheme}
            direction={direction}
            fareClass={
              fareClass !== 'no-fare'
                ? segment.marketingCarrier.fareClasses.find(({ code }) => code === fareClass)
                : undefined
            }
            fareSeats={fareSeats}
          />
        )
      })}
    </div>
  )
}

export default SeatsPreview
